import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  Search, FileText, Youtube, Share2, Globe, Palette, Sparkles, 
  Megaphone, Code, Wrench, ChevronDown, Crown, Zap, ArrowRight,
  Settings, Link as LinkIcon, TrendingUp, MapPin, Users, Edit,
  CheckCircle, BookOpen, Video, Image, Hash, Calendar, BarChart,
  MessageSquare, Target, Calculator, RefreshCw, Type, Braces, Shield, Clock,
  MessageCircle, Gift, FileBadge, Wand2, CheckSquare
} from "lucide-react";
import { MEGA_MENU_DATA, advancedSearchTools, getFeaturedTools, USE_CASES } from "@/data/toolsData";

// Icon mapping
const iconMap = {
  Search, FileText, Youtube, Share2, Globe, Palette, Sparkles,
  Megaphone, Code, Wrench, Settings, Link: LinkIcon, TrendingUp,
  MapPin, Users, Edit, CheckCircle, BookOpen, Video, Image,
  Hash, Calendar, BarChart, MessageSquare, Target, Calculator,
  RefreshCw, Type, Braces, Shield
};

// Color mapping for categories
const colorMap = {
  emerald: "bg-emerald-500",
  blue: "bg-blue-500",
  red: "bg-red-500",
  pink: "bg-pink-500",
  violet: "bg-violet-500",
  orange: "bg-orange-500",
  purple: "bg-purple-500",
  cyan: "bg-cyan-500",
  slate: "bg-slate-500",
  amber: "bg-amber-500"
};

const hoverColorMap = {
  emerald: "hover:bg-emerald-50 hover:text-emerald-700",
  blue: "hover:bg-blue-50 hover:text-blue-700",
  red: "hover:bg-red-50 hover:text-red-700",
  pink: "hover:bg-pink-50 hover:text-pink-700",
  violet: "hover:bg-violet-50 hover:text-violet-700",
  orange: "hover:bg-orange-50 hover:text-orange-700",
  purple: "hover:bg-purple-50 hover:text-purple-700",
  cyan: "hover:bg-cyan-50 hover:text-cyan-700",
  slate: "hover:bg-slate-50 hover:text-slate-700",
  amber: "hover:bg-amber-50 hover:text-amber-700"
};

export default function MegaMenu() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const menuRef = useRef(null);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('markeemix_recent_searches');
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Save search to history
  const saveToHistory = (tool) => {
    const updated = [
      { id: tool.id, name: tool.name, path: tool.path },
      ...recentSearches.filter(t => t.id !== tool.id)
    ].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('markeemix_recent_searches', JSON.stringify(updated));
  };

  // Handle search with advanced scoring
  useEffect(() => {
    if (searchQuery.length > 1) {
      const results = advancedSearchTools(searchQuery);
      setSearchResults(results.slice(0, 10));
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveCategory(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = (categoryId) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveCategory(categoryId);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveCategory(null);
    }, 300);
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || Wrench;
    return Icon;
  };

  const activeCategoryData = MEGA_MENU_DATA.find(c => c.id === activeCategory);

  return (
    <div ref={menuRef} className="relative">
      {/* Navigation Items */}
      <nav className="flex items-center gap-1">
        {/* Search Bar */}
        <div className="relative mr-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
              className="pl-9 pr-4 py-2 w-48 text-sm bg-slate-100 rounded-lg border-0 focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
            />
          </div>
          
          {/* Search Results Dropdown */}
          {isSearchFocused && searchResults.length > 0 && (
            <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50" data-testid="search-results-dropdown">
              <div className="px-4 py-2 border-b border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {searchResults.length} Tools Found
                </p>
              </div>
              {searchResults.map(tool => {
                const Icon = getIcon(tool.icon);
                return (
                  <Link
                    key={tool.id}
                    to={`${tool.path}?tool=${tool.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors group"
                    onClick={() => { saveToHistory(tool); setSearchQuery(""); }}
                    data-testid={`search-result-${tool.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center group-hover:from-purple-200 group-hover:to-pink-200 transition-colors">
                      <Icon className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 group-hover:text-purple-600 transition-colors">{tool.name}</p>
                      <p className="text-xs text-slate-500 truncate">{tool.description}</p>
                    </div>
                    {tool.isNew && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">NEW</span>
                    )}
                    {tool.isPremium && (
                      <Crown className="w-3.5 h-3.5 text-amber-500" />
                    )}
                  </Link>
                );
              })}
              <div className="px-4 py-2 border-t border-slate-100 mt-1">
                <Link 
                  to="/use-cases"
                  className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  onClick={() => setSearchQuery("")}
                >
                  <Sparkles className="w-3 h-3" />
                  Browse by Goal
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          )}

          {/* Recent Searches - show when focused but no query */}
          {isSearchFocused && searchQuery.length === 0 && recentSearches.length > 0 && (
            <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50" data-testid="recent-searches-dropdown">
              <div className="px-4 py-2 border-b border-slate-100">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Recent Searches
                </p>
              </div>
              {recentSearches.map(tool => (
                <Link
                  key={tool.id}
                  to={`${tool.path}?tool=${tool.id}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors group"
                  onClick={() => setIsSearchFocused(false)}
                  data-testid={`recent-search-${tool.id}`}
                >
                  <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                  <span className="text-sm text-slate-700 group-hover:text-purple-600">{tool.name}</span>
                </Link>
              ))}
              <div className="px-4 py-2 border-t border-slate-100 mt-1">
                <button 
                  onClick={() => { setRecentSearches([]); localStorage.removeItem('markeemix_recent_searches'); }}
                  className="text-xs text-slate-500 hover:text-red-500 font-medium"
                >
                  Clear History
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Workflows — NEW outcome-led front door */}
        <Link
          to="/workflows"
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-violet-700 bg-violet-50 hover:bg-violet-100 transition-colors"
          data-testid="megamenu-workflows"
        >
          <Sparkles className="w-4 h-4" />
          <span className="hidden xl:inline">Workflows</span>
        </Link>

        {/* Category Links - Show only first 5 (SEO, Content, YouTube, Social, Design) */}
        {MEGA_MENU_DATA.slice(0, 5).map(category => {
          const Icon = getIcon(category.icon);
          return (
            <div
              key={category.id}
              onMouseEnter={() => handleMouseEnter(category.id)}
              onMouseLeave={handleMouseLeave}
              className="relative"
            >
              <Link
                to={category.path}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeCategory === category.id
                    ? 'bg-slate-100 text-slate-900'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden xl:inline">{category.name.replace(' Tools', '')}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${activeCategory === category.id ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          );
        })}

        {/* More Dropdown */}
        <div
          onMouseEnter={() => handleMouseEnter('more')}
          onMouseLeave={handleMouseLeave}
          className="relative"
        >
          <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors">
            More
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {/* More Dropdown Menu */}
          {activeCategory === 'more' && (
            <div 
              className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50"
              onMouseEnter={() => handleMouseEnter('more')}
              onMouseLeave={handleMouseLeave}
            >
              <div className="px-3 py-2 border-b border-slate-100">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">More Tools</p>
              </div>
              {/* AI Tools */}
              <Link
                to="/tools/content?tool=ai-writer"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-purple-600">AI Tools</p>
                  <p className="text-xs text-slate-500">AI-powered marketing tools</p>
                </div>
              </Link>
              {/* Google Ads */}
              <Link
                to="/tools/google-ads"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-google-ads"
              >
                <div className="w-9 h-9 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-cyan-600">Google Ads Tools</p>
                  <p className="text-xs text-slate-500">Ad copy & campaign tools</p>
                </div>
              </Link>
              {/* Facebook Ads */}
              <Link
                to="/tools/facebook-ads"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-facebook-ads"
              >
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-blue-600">Facebook Ads Tools</p>
                  <p className="text-xs text-slate-500">Ad downloader & creative tools</p>
                </div>
              </Link>
              {/* Developer Tools */}
              <Link
                to="/tools/seo/technical"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Code className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-slate-700">Developer Tools</p>
                  <p className="text-xs text-slate-500">Technical SEO & Code</p>
                </div>
              </Link>
              {/* Utilities */}
              <Link
                to="/tools/design"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
              >
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-amber-600">Utilities</p>
                  <p className="text-xs text-slate-500">Handy utility tools</p>
                </div>
              </Link>
              {/* PDF Tools */}
              <Link
                to="/tools/design/pdf"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-pdf-tools"
              >
                <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-red-600">PDF Tools</p>
                  <p className="text-xs text-slate-500">Merge, split, compress PDFs</p>
                </div>
              </Link>
              {/* WhatsApp Tools */}
              <Link
                to="/whatsapp"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-whatsapp-tools"
              >
                <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-green-600">WhatsApp Tools</p>
                  <p className="text-xs text-slate-500">Link generator & messaging</p>
                </div>
              </Link>
              {/* AI Blog Generator */}
              <Link
                to="/ai-blog"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-ai-blog"
              >
                <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Wand2 className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-indigo-600">AI Blog Generator</p>
                  <p className="text-xs text-slate-500">Generate SEO blogs with AI</p>
                </div>
              </Link>
              {/* Free Tools */}
              <Link
                to="/free"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-free-tools"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-emerald-600">Free Tools</p>
                  <p className="text-xs text-slate-500">100% free marketing tools</p>
                </div>
              </Link>
              {/* Task Manager */}
              <Link
                to="/task-manager"
                onClick={() => setActiveCategory(null)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                data-testid="nav-task-manager"
              >
                <div className="w-9 h-9 rounded-lg bg-violet-100 flex items-center justify-center">
                  <CheckSquare className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 group-hover:text-violet-600">Task Manager</p>
                  <p className="text-xs text-slate-500">Manage marketing tasks</p>
                </div>
              </Link>
              <div className="border-t border-slate-100 mt-2 pt-2">
                <Link
                  to="/use-cases"
                  onClick={() => setActiveCategory(null)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 group-hover:text-purple-600">Browse by Goal</p>
                    <p className="text-xs text-slate-500">Find tools by use case</p>
                  </div>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Mega Menu Dropdown */}
      {activeCategory && activeCategory !== 'more' && activeCategoryData && (
        <div
          onMouseEnter={() => handleMouseEnter(activeCategory)}
          onMouseLeave={handleMouseLeave}
          className="absolute top-full left-0 mt-2 w-[700px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50"
        >
          <div className="flex">
            {/* Left Column - Subcategories */}
            <div className="w-48 bg-slate-50 p-4 border-r border-slate-200">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Categories
              </h3>
              <div className="space-y-1">
                {activeCategoryData.subcategories.map(sub => {
                  const SubIcon = getIcon(sub.icon);
                  // Get first tool of this subcategory to open
                  const firstTool = activeCategoryData.tools.find(t => t.subcategory === sub.id);
                  const subUrl = firstTool 
                    ? `${activeCategoryData.path}?tool=${firstTool.id}`
                    : activeCategoryData.path;
                  return (
                    <Link
                      key={sub.id}
                      to={subUrl}
                      onClick={() => setActiveCategory(null)}
                      className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${hoverColorMap[activeCategoryData.color]}`}
                    >
                      <SubIcon className="w-4 h-4" />
                      <span>{sub.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Right Column - Tools Grid */}
            <div className="flex-1 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Popular {activeCategoryData.name}
                </h3>
                <Link
                  to={activeCategoryData.path}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                >
                  View All <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {activeCategoryData.tools.filter(tool => !tool.comingSoon).map(tool => {
                  const ToolIcon = getIcon(tool.icon);
                  // Create URL with tool parameter for direct tool selection
                  const toolUrl = `${tool.path}?tool=${tool.id}`;
                  return (
                    <Link
                      key={tool.id}
                      to={toolUrl}
                      onClick={() => setActiveCategory(null)}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors group"
                    >
                      <div className={`w-10 h-10 rounded-lg ${colorMap[activeCategoryData.color]} bg-opacity-10 flex items-center justify-center flex-shrink-0`}>
                        <ToolIcon className={`w-5 h-5 text-${activeCategoryData.color}-600`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 transition-colors truncate">
                            {tool.name}
                          </p>
                          {tool.isPremium && <Crown className="w-3 h-3 text-amber-500" />}
                          {tool.isNew && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">NEW</span>}
                        </div>
                        <p className="text-xs text-slate-500 truncate">{tool.description}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom Banner */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Zap className="w-4 h-4" />
              <span className="text-sm font-medium">Unlock all 50+ tools with Premium</span>
            </div>
            <Link
              to="/pricing"
              className="text-sm bg-white text-indigo-600 px-4 py-1.5 rounded-lg font-medium hover:bg-indigo-50 transition-colors"
            >
              Upgrade Now
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
